import { lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { OrbitalBackground } from "./OrbitalBackground";
import { useOrbitalDataLayers } from "../hooks/useOrbitalDataLayers";

// VITE_ORBITAL_TIER=0        → canvas (OrbitalBackground, Tier 0)
// VITE_ORBITAL_TIER=3        → Three.js WebGL (OrbitalScene, Tier 3, real data)
// VITE_ORBITAL_PRESET        → north_polar | south_polar | pacific_coast (default: pacific_coast)
// unset / anything else      → original static hero
const ORBITAL_TIER = Number(import.meta.env.VITE_ORBITAL_TIER ?? 0) || 0;
const ORBITAL_PRESET = (import.meta.env.VITE_ORBITAL_PRESET as string | undefined) ?? "pacific_coast";

// Lazy-load OrbitalScene (Three.js = ~600KB) only when tier 3 is requested
const OrbitalScene = lazy(() =>
  import("three").then(() =>
    import("../../../orbital-render/src/OrbitalScene").then((m) => ({ default: m.OrbitalScene }))
  )
);

function DefaultHero() {
  return (
    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <picture>
        <source
          media="(max-width: 640px)"
          srcSet="/hero/bluepulse_pulse_hero_bottom_mobile_1290x720.webp"
          type="image/webp"
        />
        <source
          srcSet="/hero/bluepulse_pulse_hero_bottom_banner_2880x720.webp"
          type="image/webp"
        />
        <img
          src="/hero/bluepulse_pulse_hero_bottom_banner_2880x720.png"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full hero-pan"
          loading="eager"
          // lowercase attr via spread: React 18.3 doesn't recognize camelCase fetchPriority
          // (that lands in React 19), and the DOM types don't declare the lowercase form either
          {...{ fetchpriority: "high" }}
        />
      </picture>

      {/* Grade so glass cards pop */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/70 to-black/85" />

      {/* Alive layers (NO shimmer, hex only) */}
      <div className="absolute inset-0 hero-banding" />
      <div className="absolute inset-0 hero-hex" />
      <div className="absolute inset-0 hero-grain" />

      {/* Ambient blobs (subtle motion) */}
      <div className="blob w-[520px] h-[520px] left-[-140px] top-[-200px] bg-white/10" />
      <div className="blob w-[460px] h-[460px] right-[-180px] top-[60px] bg-white/10" />
      <div className="blob w-[560px] h-[560px] left-[40%] bottom-[-260px] bg-white/10" />
    </div>
  );
}


function OrbitalTier3() {
  const { kelp, sst } = useOrbitalDataLayers();
  return (
    <Suspense fallback={<DefaultHero />}>
      <OrbitalScene
        className="pointer-events-none fixed inset-0 -z-10"
        dataLayers={{ kelp, sst }}
        preset={ORBITAL_PRESET as "north_polar" | "south_polar" | "pacific_coast"}
      />
    </Suspense>
  );
}

export default function SiteBackground() {
  const { pathname: _pathname } = useLocation();
  if (ORBITAL_TIER === 3) return <OrbitalTier3 />;
  if (ORBITAL_TIER === 0 && import.meta.env.VITE_ORBITAL_BG === "true") return <OrbitalBackground active />;
  return <DefaultHero />;
}
